import { Box, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import { FormikConfig } from 'formik';
import React from 'react';
import TabsLayout from '../../../components/Tabs/TabsLayout';
import { eliminateOjectNotTruthyValues } from '../../../lib/helperFuncs';
import Theme from '../../../Themes/main.theme';
import { DataGrid, GridColDef} from '@mui/x-data-grid';
import { getAllCategoriesInfo, getAllForeasInfo, getAllTypesInfo } from '../../../lib/fetchers';
import { categoriesInfoInitialValues, typesInfoInitialValues } from '../../../lib/static/filters/initialValues';
import { categoriesInfoFields, typesInfoFields } from '../../../lib/static/filters/filterFields';
import FilterComponentV2 from '../../../components/TablegridWithFilters/FilterComponent/FilterComponentv2';
import { fetchAllCategoriesValidationSchema, fetchAllTypesValidationSchema } from '../../../lib/static/filters/ValidationSchema';

const GeneralInfo = () => {
  const [tab, setTab] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  const tab1_columns: GridColDef[] = [
    {
      field: 'foreasCode',
      headerName: 'Κωδικός φορέα',
      minWidth: 150,

    },
    {
      field: 'description',
      headerName: 'Περιγραφή',
      flex: 1,
      minWidth: 170,
    },
  ];

  const tab2_columns: GridColDef[] = [
    {
      field: 'categoryCode',
      headerName: 'Κωδικός κατηγορίας',
      minWidth: 150,

    },
    {
      field: 'description',
      headerName: 'Περιγραφή',
      flex: 1,
      minWidth: 170,
    },
  ];

  const tab3_columns: GridColDef[] = [
    {
      field: 'foreasCode',
      headerName: 'Κωδικός Κατηγορίας',
      flex: 0.3
    },
    {
      field: 'typeCode',
      headerName: 'Κωδικός Τύπου',
      flex: 0.3
    },
    {
      field: 'description',
      headerName: 'Περιγραφή',
      flex: 1,
    },
    {
      field: 'paravoloAmount',
      headerName: 'Ποσό παραβόλου (κόστος)',
      flex: 0.4
    },
    {
      field: 'additionalInfoLabel',
      headerName: 'Πρόσθετα στοιχεία',
      flex: 1,
    },
  ];

  const tabLabels = [
    {
      label: <Typography variant='h4'>Ανάκτηση Φορέων</Typography>,
    },
    {
      label: <Typography variant='h4'>Ανάκτηση Κατηγοριών</Typography>,
    },
    {
      label: <Typography variant='h4'>Ανάκτηση Τύπων</Typography>,
    }
  ];

  const onSubmitCategories = async (values: FormikConfig<any>) => {    
    setIsLoading(true);
    try {
      const response = await getAllCategoriesInfo(eliminateOjectNotTruthyValues(values));
      setError('');
      setRows(response.data.categoriesData);
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };
  
  const onSubmitTypes = async (values: FormikConfig<any>) => {    
    setIsLoading(true);
    try {
      const response = await getAllTypesInfo(eliminateOjectNotTruthyValues(values));
      setError('');
      const _data = response.data.typesData.map((data: any) => {
        return {
          ...data,
          foreasCode: response.data.foreasCode
        };
      });      
      setRows(_data);      
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const CustomDataGrid = (
    columns: GridColDef[], rowsPerPageOptions: number[], pageSize: number
  ) => {
    return (
      <DataGrid
        disableColumnMenu
        disableSelectionOnClick
        autoHeight
        getRowId={(row) => JSON.stringify(row)}
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        rowsPerPageOptions={rowsPerPageOptions}
        componentsProps={{
          pagination: {
            SelectProps: {
              MenuProps: {
                disablePortal: true,
              }
            }
          }
        }}

        loading={isLoading}
        components={{
          LoadingOverlay: LinearProgress,
          NoRowsOverlay: () => (
            <Stack height='100%' alignItems='center'
              justifyContent='center'>
              Δεν υπάρχουν εγγραφές
            </Stack>
          )
        }}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F2F2F2',
            fontSize: 16
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            textOverflow: 'clip',
            whiteSpace: 'break-spaces',
            lineHeight: 1
          },
          '& .MuiDataGrid-columnHeader: focus': {
            outline: 'none'
          },
          '& .MuiDataGrid-cell': {
            textOverflow: 'clip',
            whiteSpace: 'break-spaces',
            lineHeight: 1
          }
        }}
      />
    );
  };

  const tab1 = <Box my={4} width='100%'>
    {CustomDataGrid(tab1_columns, [15, 25, 50], 15)}
  </Box>; 

  const tab2 = <Box my={4}>
    <FilterComponentV2
      title='Φίλτρο Αναζήτησης Κατηγοριών'
      validationSchema={fetchAllCategoriesValidationSchema}
      filterFields={categoriesInfoFields}
      initialValues={categoriesInfoInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitCategories(filterValues)}
      validateOnBlur={false}
    />
    <Box mt={5}>
      {error
        ? <Typography variant='h4' display='flex'
          justifyContent='center' >
          {error}
        </Typography> 
        : rows.length > 0
          ? CustomDataGrid(tab2_columns, [15, 25, 50], 15)
          : null 
      }
    </Box>
  </Box>;

  const tab3 = <Box my={4}>
    <FilterComponentV2
      title='Φίλτρο Αναζήτησης Τύπων'
      validationSchema={fetchAllTypesValidationSchema}
      filterFields={typesInfoFields}
      initialValues={typesInfoInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitTypes(filterValues)}
      validateOnBlur={false}
    />
    <Box mt={5}>
      {error
        ? <Typography variant='h4' display='flex'
          justifyContent='center' >
          {error}
        </Typography> 
        : rows.length > 0
          ? CustomDataGrid(tab3_columns, [15, 25, 50], 15)
          : null 
      }
    </Box>
  </Box>;

  const tabContent = [
    tab1,
    tab2,
    tab3
  ];


  const getForeasInfo = async () => {
    setIsLoading(true);
    const response = await getAllForeasInfo();
    setRows(response.data.foreisData);
    setIsLoading(false);
  };

  React.useEffect(() => {
    tab === 0 &&
    getForeasInfo();
  }, [tab]);

  return (
    <Box mt={5}>
      <Typography sx={{ my: 2 }} variant='h2'
        color={Theme?.labelColor?.main}>Ανάκτηση Στοιχείων Ηλεκτρονικού Παραβόλου</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
        resetFilters={() => {
          setRows([]);
          setError('');
        }}
      />
    </Box>
  );
};

export default GeneralInfo;